import { Collection, Ref, RefObject } from '@tblabs/truffle';
import { Song } from '../Models/Song';
import { MusicPlayer } from '../Services/MusicPlayer';
import { PlaybackMode } from '../Services/PlaybackMode';
import { PlaylistProvider } from '../Services/PlaylistProvider';
import { SelectableList } from '../Models/SelectableList';
import { Playlist } from '../Models/Playlist';


export class PlayerLogic
{
    public CurrentPlaylist = new RefObject<Playlist>(Playlist.Default);
    public PlaybackMode = new Ref<PlaybackMode>(PlaybackMode.DoNotRepeat).Storable("playback-mode");

    public get AvailablePlaylists(): Collection<Playlist>
    {
        return this._playlistProvider.Playlists;
    }

    constructor(
        private _playlistProvider: PlaylistProvider,
        private _songsList: SelectableList<Song>,
        private _musicPlayer: MusicPlayer)
    { }

    public async UpdateCurrentPlaylist(): Promise<void>
    {
        const songsList = this._songsList.AsArray;
        this.CurrentPlaylist.object.Songs.Load(...songsList);
        await this._playlistProvider.UpdatePlaylist(this.CurrentPlaylist.object);
    }

    public async LoadAllSongs(): Promise<void>
    {
        const songs = await this._playlistProvider.GetAllAvailableSongs();
        this._songsList.Load(songs);
    }

    public async ExportSelectedToNewPlaylist(name: string): Promise<void>
    {
        const songs: Song[] = this._songsList.GetSelected();
        const newPlaylistId = crypto.randomUUID();
        const newPlaylist = new Playlist(newPlaylistId, name, songs);
        await this._playlistProvider.AddPlaylist(newPlaylist);
        this.CurrentPlaylist.object = newPlaylist;
    }

    public Init(): void
    {
        this.CurrentPlaylist.OnChange(async (playlist: Playlist) =>
        {
            if (playlist.Id == Playlist.Default.Id)
            {
                const allSongs = await this._playlistProvider.GetAllAvailableSongs();
                this._songsList.Load(allSongs);
                this._musicPlayer.Pause();
            }
            else 
            {
                this._songsList.Load(playlist.Songs);
                this._songsList.SelectFirst();
            }
        }, true);

        this._songsList.OnSelectionChange.Subscribe((item) =>
        {
            this._musicPlayer.Play(item.Item);
        });

        this._musicPlayer.OnSongEnd(() =>
        {
            this._songsList.Current?.IsSelected?.True();

            switch (this.PlaybackMode.value)
            {
                case PlaybackMode.RepeatAll:
                    this._songsList.Next();
                    break;
                case PlaybackMode.RepeatOne:
                    this._songsList.Select(this._songsList.Current);
                    break;
                case PlaybackMode.Shuffle:
                    this._songsList.Random();
                    break;
                default:
                case PlaybackMode.DoNotRepeat:
                    this._musicPlayer.Pause();
                    break;
            }
        });
    }

    public get StartAt(): Ref<number>
    {
        return this._musicPlayer.StartAt;
    }

    public get Volume(): Ref<number>
    {
        return this._musicPlayer.Volume;
    }

    public PlayPreviousSong(): void
    {
        this._songsList.Previous();
    }

    public get IsPlaying(): Ref<boolean>
    {
        return this._musicPlayer.IsPlaying;
    }

    public get Duration(): Ref<number>
    {
        return this._musicPlayer.Duration;
    }

    public PauseMusic(): void
    {
        this._musicPlayer.Pause();
    }

    public PlayNextSong(): void
    {
        this._songsList.Next();
    }

    public async Play(): Promise<void>
    {
        if (!this._songsList.Current)
        {
            this._songsList.Next();
        }

        await this._musicPlayer.Play();
    }
}
